import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

const EXPANDED_CLASS = "trf-searchDispoContainerB2B--expanded";
const MOBILE_MQ = "(max-width: 767px)";

/**
 * Alterna entre `ComSearchB2BNonEditable` y el buscador editable
 * (`TrfSearchB2BForm`) en la disponibilidad de transfers B2B.
 * Desktop: clic en el buscador colapsado lo expande in-place; clic en la X
 * de `TrfSearchB2BForm` lo cierra. Mobile: clic abre en su lugar el modal
 * `TrfSearchB2BDispoModal` (enlazado vía `data-search-modal-id`) — mismo
 * patrón que `flySearchDispoToggle` en vuelos.
 */
export class TrfSearchDispoToggle implements Plugin {
  private trigger: HTMLButtonElement | null = null;
  private closeButton: HTMLButtonElement | null = null;
  private nonEditableView: HTMLElement | null = null;
  private editableView: HTMLElement | null = null;
  private modal: HTMLElement | null = null;
  private isExpanded = false;
  private mobileMq: MediaQueryList | null = null;
  private clickHandler: ((event: Event) => void) | null = null;
  private closeButtonHandler: (() => void) | null = null;
  private keydownHandler: ((event: KeyboardEvent) => void) | null = null;
  private mqHandler: ((event: MediaQueryListEvent) => void) | null = null;

  constructor(private readonly element: HTMLElement) {}

  init(): void {
    this.trigger = this.element.querySelector(
      ".com-searchB2BNonEditable .com-searchButtonB2B__button",
    );
    this.nonEditableView = this.element.querySelector(
      '[data-search-view="non-editable"]',
    );
    this.editableView = this.element.querySelector(
      '[data-search-view="editable"]',
    );

    const modalId = this.element.dataset.searchModalId;
    this.modal = modalId ? document.getElementById(modalId) : null;
    this.portalModalToBody();

    if (!this.trigger || !this.editableView) return;

    this.clickHandler = (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (this.isMobile()) {
        this.modal?.dispatchEvent(new CustomEvent("modal:open"));
        return;
      }

      this.expand();
    };
    this.trigger.addEventListener("click", this.clickHandler);

    this.closeButton = this.editableView.querySelector<HTMLButtonElement>(
      ".trf-searchB2BForm__close",
    );
    if (this.closeButton) {
      this.closeButtonHandler = () => this.collapse();
      this.closeButton.addEventListener("click", this.closeButtonHandler);
    }

    this.keydownHandler = (event) => {
      if (event.key === "Escape" && this.isExpanded) this.collapse();
    };
    document.addEventListener("keydown", this.keydownHandler);

    if (typeof window.matchMedia === "function") {
      this.mobileMq = window.matchMedia(MOBILE_MQ);
      this.mqHandler = (event) => {
        if (event.matches && this.isExpanded) this.collapse();
      };
      this.mobileMq.addEventListener("change", this.mqHandler);
    }
  }

  destroy(): void {
    if (this.trigger && this.clickHandler) {
      this.trigger.removeEventListener("click", this.clickHandler);
    }
    if (this.closeButton && this.closeButtonHandler) {
      this.closeButton.removeEventListener("click", this.closeButtonHandler);
    }
    if (this.keydownHandler) {
      document.removeEventListener("keydown", this.keydownHandler);
    }
    if (this.mobileMq && this.mqHandler) {
      this.mobileMq.removeEventListener("change", this.mqHandler);
    }
  }

  private isMobile(): boolean {
    return window.matchMedia(MOBILE_MQ).matches;
  }

  /**
   * La modal vive junto al buscador, dentro de un contenedor que puede tener
   * transform/will-change (animaciones del toggle in-place) que rompen
   * `position: fixed`. La movemos a `body`, igual que `flySearchDispoToggle`.
   */
  private portalModalToBody(): void {
    if (!this.modal || this.modal.dataset.portaled === "true") return;

    document.body.appendChild(this.modal);
    this.modal.dataset.portaled = "true";
  }

  private expand(): void {
    if (this.isExpanded || !this.nonEditableView || !this.editableView) return;
    this.isExpanded = true;
    this.editableView.removeAttribute("hidden");
    this.element.classList.add(EXPANDED_CLASS);
  }

  private collapse(): void {
    if (!this.isExpanded || !this.editableView) return;
    this.isExpanded = false;
    this.element.classList.remove(EXPANDED_CLASS);
    this.editableView.setAttribute("hidden", "");
  }
}

PluginRegistry.register("trfSearchDispoToggle", TrfSearchDispoToggle);
